import { useEffect } from 'react'
import { DEVICE_AUTH_INACTIVITY_MINUTES } from '@/common/env'
import {
    clearDeviceAuthSession,
    getDeviceAuthVerified,
    getLastDeviceAuthActivityTs,
    touchDeviceAuthActivity,
} from './storage'
import { isInactivityExpired } from './policy'

type UseDeviceAuthActivityTimeoutProps = {
    enabled: boolean
    inactivityMs?: number
    onExpired?: () => void
}

export function useDeviceAuthActivityTimeout({
    enabled,
    inactivityMs = +DEVICE_AUTH_INACTIVITY_MINUTES * 60 * 1000,
    onExpired,
}: UseDeviceAuthActivityTimeoutProps) {
    useEffect(() => {
        if (typeof window === 'undefined') return
        if (!enabled) return

        const onActivity = () => {
            try {
                if (!getDeviceAuthVerified()) return
                if (document.visibilityState === 'hidden') return
                touchDeviceAuthActivity()
            } catch {
                // ignore
            }
        }

        const checkInactivity = () => {
            try {
                if (!getDeviceAuthVerified()) return
                const lastActivityTs = getLastDeviceAuthActivityTs()
                if (isInactivityExpired(lastActivityTs, inactivityMs)) {
                    clearDeviceAuthSession()
                    if (onExpired) {
                        onExpired()
                    }
                }
            } catch {
                // ignore
            }
        }

        const windowEvents: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'click', 'touchstart', 'focus']

        windowEvents.forEach((eventName) => {
            window.addEventListener(eventName, onActivity, { passive: true })
        })
        document.addEventListener('visibilitychange', onActivity)

        const intervalId = window.setInterval(checkInactivity, 30_000)

        return () => {
            windowEvents.forEach((eventName) => {
                window.removeEventListener(eventName, onActivity)
            })
            document.removeEventListener('visibilitychange', onActivity)
            window.clearInterval(intervalId)
        }
    }, [enabled, inactivityMs, onExpired])
}
